import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Tabs,
    Tab,
    Paper,
    Alert,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    CircularProgress,
    IconButton
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    Add as AddIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { AppointmentList } from "components/appointments";
import * as Actions from "actions";
import UnifiedPullToRefresh from "components/elements/main/UnifiedPullToRefresh";
import ShopHeaderMini from "components/elements/main/ShopHeaderMini";
import { isMarketPlace, getURI } from "helpers/utils";

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`shop-appointments-tabpanel-${index}`}
            aria-labelledby={`shop-appointments-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

const ShopUserAppointments = (props) => {
    const { 
        match, 
        bootupInfo, 
        history,
        userAppointments = [],
        userAppointmentsLoading = false,
        userAppointmentsError = null,
        userAppointmentsPagination = {
            current_page: 1,
            total_pages: 1,
            total_appointments: 0,
            per_page: 10
        },
        timezones = [],
        actionSuccess = false,
        getUserAppointments,
        getTimezones,
        clearAppointmentSuccess
    } = props;
    
    const [tabValue, setTabValue] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [allTabStatusFilter, setAllTabStatusFilter] = useState(''); // Separate filter for "All" tab dropdown
    const [userTimezone, setUserTimezone] = useState(moment.tz.guess());
    
    // Get shop ID from URL params or subdomain
    const shopId = isMarketPlace()
        ? match.params.shopId
        : bootupInfo.appState.subdomain_shopId;
    
    useEffect(() => {
        // Fetch timezones on component mount
        getTimezones();
        
        // Fetch appointments
        fetchAppointments();
    }, [statusFilter, userTimezone, shopId, allTabStatusFilter]);
    
    useEffect(() => {
        // Refetch appointments when an action is successful (cancel, etc.)
        if (actionSuccess) {
            fetchAppointments();
            clearAppointmentSuccess();
        }
    }, [actionSuccess]);
    
    const fetchAppointments = () => {
        const params = {
            user_timezone: userTimezone,
            shop: shopId // Add shop ID to filter appointments by shop
        };
        
        // Use dropdown filter for "All" tab, or tab-based filter for other tabs
        if (tabValue === 1 && allTabStatusFilter) {
            // "All" tab with dropdown filter selected
            params.status = allTabStatusFilter;
        } else if (statusFilter) {
            // Other tabs with tab-based filter
            params.status = statusFilter;
        }
        
        getUserAppointments(params);
    };
    
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        
        // Clear the "All" tab filter when switching to other tabs
        if (newValue !== 1) {
            setAllTabStatusFilter('');
        }
        
        // Update status filter based on tab
        switch (newValue) {
            case 1: // All appointments (excluding cancelled)
                setStatusFilter('');
                break;
            case 0: // Upcoming
                setStatusFilter('scheduled,confirmed');
                break;
            default:
                setStatusFilter('');
        }
    };
    
    const handleBookNewAppointment = () => {
        // Navigate to appointment booking for this specific shop
        history.push(getURI(process.env.REACT_APP_TYPE, "shop_appointment_book", shopId));
    };
    
    const handleRefresh = () => {
        fetchAppointments();
    };
    
    // Since API now filters by shop ID, userAppointments already contains only shop-specific appointments
    const getFilteredAppointments = () => {
        switch (tabValue) {
            case 0: // Upcoming
                return getUpcomingAppointments();
            default: // All - API already filtered by status
                // API handles filtering, just return the appointments
                return userAppointments;
        }
    };
    
    const getUpcomingAppointments = () => {
        const now = new Date();
        return userAppointments.filter(apt => 
            new Date(apt.start_time) > now && 
            ['scheduled', 'confirmed'].includes(apt.status)
        );
    };
    
    
    const getStatusCounts = () => {
        return {
            total: userAppointments.filter(apt => apt.status !== 'cancelled').length,
            upcoming: getUpcomingAppointments().length,
            scheduled: userAppointments.filter(apt => apt.status === 'scheduled').length,
            confirmed: userAppointments.filter(apt => apt.status === 'confirmed').length,
            completed: userAppointments.filter(apt => apt.status === 'completed').length
        };
    };
    
    const statusCounts = getStatusCounts();
    const shopInfo = userAppointments.length > 0 ? userAppointments[0].store_id : null;
    
    const renderEmpty = () => (
        <Box textAlign="center" py={6}>
            <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
                No appointments found
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
                {shopInfo ? `You haven't booked any appointments with ${shopInfo.title} yet.` : "You haven't booked any appointments with this shop yet."}
            </Typography>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleBookNewAppointment}
            >
                Book Your First Appointment
            </Button>
        </Box>
    );
    
    return (
        <UnifiedPullToRefresh
            onRefresh={handleRefresh}
            isRefreshing={userAppointmentsLoading}
            refreshText={{
                pull: 'Pull to refresh appointments',
                release: 'Release to refresh appointments',
                refreshing: 'Refreshing appointments...'
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            onClick={handleRefresh}
                            disabled={userAppointmentsLoading}
                            title="Refresh appointments"
                        >
                            {userAppointmentsLoading ? <CircularProgress size={24} /> : <RefreshIcon />}
                        </IconButton>

                    </Box>
                </Box>
                

                <Paper sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="shop appointment tabs">
                            <Tab
                                label={`Upcoming (${statusCounts.upcoming})`}
                                icon={<ScheduleIcon />}
                                iconPosition="start"
                            />
                            <Tab 
                                label={`All (${statusCounts.total})`} 
                                icon={<ScheduleIcon />} 
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        {getUpcomingAppointments().length === 0 ? (
                            <Box textAlign="center" py={6}>
                                <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    No upcoming appointments
                                </Typography>
                                <Typography variant="body2" color="textSecondary" mb={3}>
                                    {shopInfo ? `Book an appointment with ${shopInfo.title} to see it here.` : "Book an appointment with this shop to see it here."}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleBookNewAppointment}
                                >
                                    Book Appointment
                                </Button>
                            </Box>
                        ) : (
                            <AppointmentList
                                userType="user"
                                appointments={getUpcomingAppointments()}
                                loading={userAppointmentsLoading}
                                onRefresh={fetchAppointments}
                                showShopInfo={false}
                                hideFilter={true}
                            />
                        )}
                    </TabPanel>
                    
                    <TabPanel value={tabValue} index={1}>
                        {userAppointmentsError ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {userAppointmentsError}
                            </Alert>
                        ) : null}
                        
                        <AppointmentList
                            userType="user"
                            appointments={getFilteredAppointments()}
                            loading={userAppointmentsLoading}
                            pagination={userAppointmentsPagination}
                            onRefresh={fetchAppointments}
                            showShopInfo={false} // Don't show shop info since it's shop-specific
                            hideFilter={false}
                            externalStatusFilter={allTabStatusFilter}
                            onStatusFilterChange={setAllTabStatusFilter}
                        />
                    </TabPanel>

                    
                </Paper>
            </Container>
        </UnifiedPullToRefresh>
    );
};

ShopUserAppointments.propTypes = {
    match: PropTypes.object.isRequired,
    bootupInfo: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    userAppointments: PropTypes.arrayOf(PropTypes.object),
    userAppointmentsLoading: PropTypes.bool,
    userAppointmentsError: PropTypes.string,
    userAppointmentsPagination: PropTypes.object,
    timezones: PropTypes.arrayOf(PropTypes.object),
    actionSuccess: PropTypes.bool,
    getUserAppointments: PropTypes.func.isRequired,
    getTimezones: PropTypes.func.isRequired,
    clearAppointmentSuccess: PropTypes.func.isRequired,
};

const shopUserAppointments = connect(
    ({ appointments, bootupInfo }) => ({
        bootupInfo,
        userAppointments: appointments?.userAppointments || [],
        userAppointmentsLoading: appointments?.userAppointmentsLoading || false,
        userAppointmentsError: appointments?.userAppointmentsError || null,
        userAppointmentsPagination: appointments?.userAppointmentsPagination || {
            current_page: 1,
            total_pages: 1,
            total_appointments: 0,
            per_page: 10
        },
        timezones: appointments?.timezones || [],
        actionSuccess: appointments?.actionSuccess || false,
    }),
    (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(ShopUserAppointments);

export { shopUserAppointments as default };
